import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

function Header() {
  const [user, setUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);

  // Color Palette Reference
  const colors = {
      darkGreen: '#06362D',
      calPolyGreen: '#155332',
      vanilla: '#DEE2B1',
      gold: '#FFD500',
      goldenrod: '#D1A505'
  };

  // 1. Listen for Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  // 2. Fetch extra user details from Firestore when the user logs in
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (!user) {
          setDbUser(null);
          return;
        }
        const userRef = doc(db, "authroles", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setDbUser(userSnap.data());
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, [user]);

  const signOut = () => {
    auth.signOut();
    window.location.href = "/login";
  };

  // Fallbacks for display data
  const displayName = dbUser?.name || user?.displayName || "Admin User";
  const displayEmail = dbUser?.email || user?.email || "pms_admin";
  
  // Custom generated avatar colors matching the new theme
  const avatarBg = colors.gold.replace('#', '');
  const avatarColor = colors.darkGreen.replace('#', '');
  const avatarUrl = user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=${avatarBg}&color=${avatarColor}`;

  return (
    <header className="dashboard-header" style={styles.header}>
      {/* Left side: User Profile & Stats */}
      <div style={styles.leftSection}>

        {/* User Profile */}
        <div style={styles.userProfile}>
          <img
            src={avatarUrl}
            alt="User"
            style={styles.avatar}
          />
          <div style={styles.userInfo}>
            <span style={styles.userName}>{displayName}</span>
            <span style={styles.userHandle}>{displayEmail}</span>
          </div>
        </div>

        {/* You can uncomment these when you want to display the stats again */}
        {/* Card: Property Status */}
        {/* <div style={styles.statCard}>
          <div style={styles.statTopRow}>
            <span style={styles.statLabel}>Property Status</span>
            <span style={styles.trendUp}>↑ 100%</span>
          </div>
          <span style={styles.statValue}>Active</span>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statTopRow}>
            <span style={styles.statLabel}>Today's Bookings</span>
            <span style={styles.trendDown}>↓ 2%</span>
          </div>
          <span style={styles.statValue}>12</span>
        </div> */}

      </div>

      {/* Right side: Actions */}
      <div style={styles.rightSection}>
        <button style={styles.iconCardButton} onClick={() => window.history.back()} title="Go Back">
          <i className="fas fa-backward" />
        </button>

        <button style={styles.iconCardButton} onClick={() => window.history.forward()} title="Go Forward">
          <i className="fas fa-forward" />
        </button>

        <button style={styles.iconCardButton} onClick={() => window.location.reload()} title="Refresh">
          <i className="fas fa-sync-alt" />
        </button>

        <button style={styles.iconCardButton} title="Notifications">
          <i className="fas fa-bell" />
          <span style={styles.notificationDot}></span>
        </button>

        <button onClick={signOut} style={styles.primaryCardButton}>
          Sign Out <i className="fa fa-sign-out-alt" style={{ marginLeft: "8px" }} />
        </button>
      </div>
    </header>
  );
}

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    background: "#06362D", // Dark Green
    color: "#ffffff",
    borderBottom: "1px solid rgba(222, 226, 177, 0.1)", // Vanilla with low opacity
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  userProfile: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginRight: "12px",
    padding: "6px 16px 6px 6px",
    background: "rgba(222, 226, 177, 0.03)",
    borderRadius: "30px",
    border: "1px solid rgba(222, 226, 177, 0.08)",
  },
  avatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
  },
  userName: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#ffffff",
    letterSpacing: "0.3px",
  },
  userHandle: {
    fontSize: "12px",
    color: "#DEE2B1", // Vanilla
    opacity: 0.8,
    marginTop: "2px",
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#155332", // Cal Poly Green
    padding: "10px 16px",
    borderRadius: "12px",
    border: "1px solid rgba(255, 213, 0, 0.1)", // Subtle Gold border
    minWidth: "120px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
  statTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
    gap: "12px",
  },
  statLabel: {
    fontSize: "11px",
    color: "#DEE2B1", // Vanilla
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#ffffff",
  },
  trendUp: {
    color: "#FFD500", // Gold
    fontSize: "11px",
    fontWeight: "700",
  },
  trendDown: {
    color: "#ff6b6b", // Kept standard red for down trend, though you could adapt if needed
    fontSize: "11px",
    fontWeight: "700",
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  iconCardButton: {
    backgroundColor: "rgba(21, 83, 50, 0.4)", // Cal Poly Green transparent
    border: "1px solid rgba(222, 226, 177, 0.15)", // Vanilla border
    color: "#DEE2B1", // Vanilla icon
    fontSize: "14px",
    cursor: "pointer",
    position: "relative",
    transition: "all 0.2s ease",
    borderRadius: "12px",
    width: "42px",
    height: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  notificationDot: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "7px",
    height: "7px",
    backgroundColor: "#FFD500", // Gold
    borderRadius: "50%",
    boxShadow: "0 0 0 2px #06362D", // Match header background
  },
  primaryCardButton: {
    backgroundColor: "#FFD500", // Gold
    color: "#06362D", // Dark Green
    border: "none",
    padding: "0 24px",
    height: "42px",
    borderRadius: "12px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    marginLeft: "8px",
    boxShadow: "0 4px 12px rgba(255, 213, 0, 0.2)",
  }
};

export default Header;